import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { reportablePageAddress, reportableSearch } from './AnalyticsPageAddress';

const STORAGE_KEY = 'museflow.conversion-attribution.v1';
const SENT_KEY = 'museflow.conversions-sent.v1';
const CAMPAIGN_COOKIE = 'mf_campaign_v1';
const ATTRIBUTION_MAX_AGE = 90 * 24 * 60 * 60 * 1000;
const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/conversion-tracking`;

export type ConversionKind =
  | 'account_created'
  | 'trial_started'
  | 'payment_method_added'
  | 'purchase'
  | 'subscription_payment'
  | 'subscription_renewal';

export type ConfirmedConversion = {
  id: string;
  kind: ConversionKind;
  occurred_at: string;
  transaction_id?: string;
  value?: number;
  currency?: string;
};

export type ConversionAttribution = {
  captured_at: number;
  platform: string;
  tracking_allowed: boolean;
  landing_page: string;
  referrer: string;
  campaign: Record<string, string>;
  reddit_click_id?: string;
  reddit_uuid?: string;
  ga_client_id?: string;
};

const canTrack = (): boolean =>
  !(navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl &&
  navigator.doNotTrack !== '1';

const readStorage = <T>(key: string): T | undefined => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
};

const writeStorage = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Tracking must never interrupt signup when browser storage is unavailable.
  }
};

const cookie = (name: string): string | undefined => {
  const value = document.cookie.split('; ').find((entry) => entry.startsWith(`${name}=`));
  return value?.slice(name.length + 1);
};

/** The attribution last captured on this device, read without capturing again. */
export const readConversionAttribution = (): ConversionAttribution | undefined =>
  readStorage<ConversionAttribution>(STORAGE_KEY);

/** Capture before authentication redirects remove the campaign parameters. */
export const captureConversionAttribution = (): ConversionAttribution => {
  let shared: ConversionAttribution | undefined;
  try {
    shared = JSON.parse(decodeURIComponent(cookie(CAMPAIGN_COOKIE) || 'null')) || undefined;
  } catch {
    /* Ignore malformed campaign cookies. */
  }
  const local = readStorage<ConversionAttribution>(STORAGE_KEY);
  const saved = shared && (!local || shared.captured_at > local.captured_at) ? shared : local;
  const search = new URLSearchParams(reportableSearch(window.location.search));
  const campaign = Object.fromEntries(
    Array.from(search).filter(([name]) => name.toLowerCase().startsWith('utm_')),
  );
  const clickId = search.get('rdt_cid') || undefined;
  const address = reportablePageAddress(window.location.href, document.referrer);
  const fresh =
    saved &&
    saved.captured_at <= Date.now() &&
    Date.now() - saved.captured_at < ATTRIBUTION_MAX_AGE;
  const newCampaign =
    Boolean(clickId || Object.keys(campaign).length) &&
    (!fresh ||
      clickId !== saved.reddit_click_id ||
      JSON.stringify(campaign) !== JSON.stringify(saved.campaign));
  const gaCookie = cookie('_ga');
  const attribution: ConversionAttribution = {
    ...(fresh && !newCampaign ? saved : {}),
    captured_at: fresh && !newCampaign ? saved.captured_at : Date.now(),
    platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web',
    tracking_allowed: canTrack(),
    landing_page: fresh && !newCampaign ? saved.landing_page : address.page_location,
    referrer: fresh && !newCampaign ? saved.referrer : address.page_referrer,
    campaign: fresh && !newCampaign ? saved.campaign : campaign,
    reddit_click_id: clickId || (fresh && !newCampaign ? saved.reddit_click_id : undefined),
    reddit_uuid: cookie('_rdt_uuid') || (fresh ? saved.reddit_uuid : undefined),
    ga_client_id: gaCookie?.match(/^GA\d+\.\d+\.(\d+\.\d+)$/)?.[1] || saved?.ga_client_id,
  };
  writeStorage(STORAGE_KEY, attribution);
  if (
    canTrack() &&
    (window.location.hostname === 'museflow.ai' ||
      window.location.hostname.endsWith('.museflow.ai'))
  ) {
    const carry = {
      captured_at: attribution.captured_at,
      landing_page: attribution.landing_page.split('?')[0],
      referrer: attribution.referrer,
      campaign: attribution.campaign,
      reddit_click_id: attribution.reddit_click_id,
    };
    const encoded = encodeURIComponent(JSON.stringify(carry));
    if (encoded.length < 3500)
      document.cookie = `${CAMPAIGN_COOKIE}=${encoded}; Domain=museflow.ai; Path=/; Max-Age=${ATTRIBUTION_MAX_AGE / 1000}; SameSite=Lax; Secure`;
  }
  return attribution;
};

/** GTM is loaded only on a clean web URL, never on a verification/OAuth callback. */
export const initializeConversionTags = (): void => {
  const container = process.env.REACT_APP_GTM_CONTAINER_ID;
  const params = new URLSearchParams(window.location.search);
  if (
    process.env.REACT_APP_NODE_ENV !== 'prd' ||
    Capacitor.isNativePlatform() ||
    !canTrack() ||
    !container?.match(/^GTM-[A-Z0-9]+$/) ||
    window.location.pathname === '/verify' ||
    params.has('code') ||
    params.has('confirmation_code') ||
    document.getElementById('museflow-conversion-gtm') ||
    document.querySelector(`script[src="https://www.googletagmanager.com/gtm.js?id=${container}"]`)
  )
    return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.id = 'museflow-conversion-gtm';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${container}`;
  document.head.appendChild(script);
};

const memorySent = new Set<string>();
const flights = new Map<string, Promise<void>>();

/** Only backend-confirmed business events are eligible; URLs and clicks cannot create them. */
export const reportConfirmedConversion = (conversion: ConfirmedConversion): boolean => {
  if (process.env.REACT_APP_NODE_ENV !== 'prd' || Capacitor.isNativePlatform() || !canTrack()) {
    return false;
  }
  const saved = readStorage<string[]>(SENT_KEY) || [];
  // Older releases stored the bare ID after sending both browser destinations.
  if (saved.includes(conversion.id)) return true;
  window.dataLayer = window.dataLayer || [];
  const fields = {
    conversion_id: conversion.id,
    event_id: conversion.id,
    ...(conversion.transaction_id ? { transaction_id: conversion.transaction_id } : {}),
    ...(conversion.value !== undefined ? { value: conversion.value } : {}),
    ...(conversion.currency ? { currency: conversion.currency } : {}),
  };
  // Revenue is sent by the backend's Measurement Protocol delivery, including
  // automatic post-trial payments. Sending it here as well would double-count it.
  const googleName = {
    account_created: 'sign_up',
    trial_started: 'start_trial',
    payment_method_added: 'add_payment_info',
    purchase: undefined,
    subscription_renewal: undefined,
    subscription_payment: undefined,
  }[conversion.kind];
  const googleReady = Boolean(
    googleName &&
    process.env.REACT_APP_G_ANALYTICS_MEASUREMENT_ID?.match(/^G-[A-Z0-9]+$/) &&
    window.gtag,
  );
  const redditReady = Boolean(process.env.REACT_APP_GTM_CONTAINER_ID?.match(/^GTM-[A-Z0-9]+$/));
  if (!googleReady && !redditReady) return false;
  const alreadySent = (destination: string) =>
    memorySent.has(`${destination}:${conversion.id}`) ||
    saved.includes(`${destination}:${conversion.id}`);
  const remember = (destination: string) => {
    const key = `${destination}:${conversion.id}`;
    memorySent.add(key);
    saved.push(key);
  };
  if (googleReady && !alreadySent('google')) {
    window.gtag('event', googleName, fields);
    if (conversion.kind === 'trial_started') {
      window.gtag('event', 'conversion', {
        send_to: 'AW-11476609062/V4t3CKjdiJcZEKbQvOAq',
        transaction_id: conversion.id,
      });
    }
    remember('google');
  }
  if (redditReady && !alreadySent('reddit')) {
    window.dataLayer.push({ event: `museflow_${conversion.kind}`, ...fields });
    remember('reddit');
  }
  writeStorage(SENT_KEY, saved.slice(-500));
  return true;
};

export const syncConfirmedConversions = (
  userId: string,
  getToken: () => Promise<string | undefined>,
): Promise<void> => {
  const existing = flights.get(userId);
  if (existing) return existing;
  const flight = (async () => {
    const token = await getToken();
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    await axios.put(`${API}/attribution`, captureConversionAttribution(), { headers });
    const response = await axios.get<ConfirmedConversion[]>(`${API}/events`, { headers });
    for (const conversion of response.data) {
      if (reportConfirmedConversion(conversion)) {
        await axios.post(
          `${API}/events/${encodeURIComponent(conversion.id)}/browser-ack`,
          {},
          { headers },
        );
      }
    }
  })()
    .catch(() => {
      // The backend retains events for retry. A tracking outage must not block the product.
    })
    .finally(() => flights.delete(userId));
  flights.set(userId, flight);
  return flight;
};
